import React, { createContext, useContext } from 'react';
import { useGoogleCursorSearch, useGoogleSearchState } from '../../hooks/useGoogleSearch';
import type { GoogleSearchState } from '../../hooks/useGoogleSearch';
import type { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';

export type GoogleSearchContextValue = {
  hits: Array<AlgoliaProductHit>;
  featuredProducts: Array<AlgoliaProductHit>;
  nbHits: number;
  facets: Record<string, Record<string, number>>;
  facetsStats: Record<string, { min: number; max: number }>;
  attributionToken?: string;
  isLoading: boolean;
  isFetching: boolean;
  loadedPageCount: number;
  isLastPage: boolean;
  showMore: () => void;
  state: GoogleSearchState;
  setHitsPerPage: (hitsPerPage: number) => void;
  setFacet: (name: string, value: string, checked: boolean) => void;
  setPriceRange: (min: number, max: number) => void;
  clearPriceRange: () => void;
  setSort: (sortBy: string | undefined, order: 'asc' | 'desc' | undefined) => void;
  setSaleItems: (v: boolean) => void;
  setClearanceItems: (v: boolean) => void;
  setExcludeMarketplace: (v: boolean) => void;
  clearAllFacets: () => void;
};

const GoogleSearchContext = createContext<GoogleSearchContextValue | null>(null);

export const useGoogleSearchContext = (): GoogleSearchContextValue => {
  const ctx = useContext(GoogleSearchContext);
  if (!ctx) throw new Error('useGoogleSearchContext must be used inside <GoogleInstantSearch>');
  return ctx;
};

type GoogleInstantSearchProps = {
  query?: string;
  filter?: string;
  requestedFacets?: Array<string>;
  featuredProductIds?: Array<string>;
  category?: string;
  children: React.ReactNode;
};

const GoogleInstantSearch = ({
  query,
  filter,
  requestedFacets,
  featuredProductIds,
  category,
  children,
}: GoogleInstantSearchProps) => {
  const {
    state,
    setHitsPerPage,
    setFacet,
    setPriceRange,
    clearPriceRange,
    setSort,
    setSaleItems,
    setClearanceItems,
    setExcludeMarketplace,
    clearAllFacets,
  } = useGoogleSearchState();

  const {
    hits,
    featuredProducts,
    nbHits,
    facets,
    facetsStats,
    attributionToken,
    hasNextPage,
    isLoading,
    isFetching,
    showMore,
    loadedPageCount,
  } = useGoogleCursorSearch({
    query,
    filter,
    requestedFacets,
    hitsPerPage: state.hitsPerPage,
    facets: state.facets,
    sortBy: state.sortBy,
    order: state.order,
    isSaleItems: state.isSaleItems,
    isClearanceItems: state.isClearanceItems,
    isExcludeMarketplace: state.isExcludeMarketplace,
    isEnabled: !!(query || filter),
    featuredProductIds,
    category,
  });
  const isLastPage = !hasNextPage;

  return (
    <GoogleSearchContext.Provider
      value={{
        hits,
        featuredProducts,
        nbHits,
        facets,
        facetsStats,
        attributionToken,
        isLoading,
        isFetching,
        loadedPageCount,
        isLastPage,
        showMore,
        state,
        setHitsPerPage,
        setFacet,
        setPriceRange,
        clearPriceRange,
        setSort,
        setSaleItems,
        setClearanceItems,
        setExcludeMarketplace,
        clearAllFacets,
      }}
    >
      {children}
    </GoogleSearchContext.Provider>
  );
};

export default GoogleInstantSearch;
